/**
 * „Vespera, schalte … ein“ — Einstellungen auf Zuruf ändern. (02.10.2026, Ticket 2914)
 *
 * Martin, 02.10.2026, Sprechtaste: *„Ich möchte, dass Vespera die Einstellungen
 * selber ändern können soll, nicht nur öffnen. Wenn ich ihm sage, er soll es
 * bitte ändern.“* Bis heute öffnete `open_view` nur die Seite.
 *
 * Diese Datei ist der REINE Teil (kein Speicher, keine Oberfläche): die weiße
 * Liste, die harten Sperren, das Lesen eines Werts und die Sätze. Das Werkzeug
 * (tools/einstellung-aendern.ts) schreibt; sie entscheidet nur, WAS und WIE.
 *
 * ## Die Regeln
 *
 * 1. Nur, was hier in `EINSTELLUNGEN` steht. Jeder Eintrag benennt die Felder
 *    von `users.settings`, die er schreibt — sonst keines (Test: jedes Feld
 *    ist entweder freigegeben oder in `GESPERRTE_FELDER`, nie beides, nie
 *    keins).
 * 2. Nur Kontowerte, die der Server selbst mit der Oberfläche teilt. Was an
 *    DIESEM Gerät hängt (Wach-Wort, Lautsprecher, Tagesaufnahme), liegt im
 *    Browser des Geräts: der Server kann es nicht umstellen und tut nicht so.
 * 3. Nur, wo ein Irrtum harmlos ist und mit einem Zuruf zurückgeht.
 * 4. Nie Zugangsdaten, Konto, Löschen, Kopplungen, Modelle und Kosten, die
 *    Grenzen der Anrufe und die Versuchsschalter am Ton- und Antwortweg
 *    (`HART_GESPERRT`) — auch nicht, wenn der Name genau stimmt.
 * 5. Was Schutz abschaltet oder nach außen wirkt, geht höchstens zweistufig
 *    (`heikel`): Rückfrage, und das Ja erst in einer späteren Nachricht.
 *
 * ⚠️ Die Namen der Oberfläche stehen hier ein zweites Mal (Stimmen, Tempo-
 *    Namen). Ein Test hält sie gegen frontend/lib/settings.ts und
 *    schnell-einstellungen.ts; wer dort etwas ändert, merkt es hier.
 */
import { EINST_SEITEN, type SeitenId } from "./einst-seiten.js";
import { EMOJI_STUFEN, emojiStufe, emojiStufenName, istEmojiStufe, type EmojiStufenName } from "./emoji-stufe.js";
import { JAHRESZEIT_NAME, JAHRESZEIT_VORGABE, JAHRESZEITEN } from "./jahreszeit.js";
import { MUSIK_OHNE_PROBE_FELD } from "./musik-ohne-probe.js";
import { VORLESER_NATIV_FELD } from "./vorleser-nativ.js";
import { SENDE_STILLE_STUFEN, STILLE_AUTO_MS, sendeStilleLesen } from "./stille-auto.js";
import { VORLESE_TEMPO, vorleseTempo } from "./vorlese-tempo.js";

export const EINSTELLUNG_AENDERN = "einstellung_aendern";

/** Der Name des SSE-Ereignisses, mit dem der Server offene Seiten zum Nachladen auffordert. */
export const EINSTELLUNG_EREIGNIS = "einstellung";

/**
 * Aus diesen Kanälen darf eine Einstellung GEÄNDERT werden: nur aus der App
 * (Sprechtaste, Chat). Nicht aus Telegram (dort kommen Weitergeleitetes und
 * Anhänge an), nicht aus geplanten Aufträgen und Hintergrundläufen, nicht aus
 * dem Anruf (dort gibt es keinen Schutz vor eingeschleusten Anweisungen).
 * Lesen geht überall.
 */
export const EINSTELLUNG_KANAELE: ReadonlySet<string> = new Set(["app"]);

/** Die Meldung des Servers an offene Seiten: was sich geändert hat — nie die Werte. */
export interface EinstellungsMeldung {
  felder: string[];
}

const FELD_FORM = /^[A-Za-z][A-Za-z0-9]{0,40}$/;

/** Die Meldung aus einem SSE-Frame — nur die erwartete Form, sonst null. */
export function leseEinstellungsMeldung(roh: unknown): EinstellungsMeldung | null {
  if (!roh || typeof roh !== "object") return null;
  const f = (roh as { felder?: unknown }).felder;
  if (!Array.isArray(f) || f.length === 0 || f.length > 20) return null;
  if (!f.every((x): x is string => typeof x === "string" && FELD_FORM.test(x))) return null;
  return { felder: f };
}

/* ── Schreibweisen vergleichen ──────────────────────────────────────── */

/** Kleinschrift, Umlaute aufgelöst, alles Übrige ein Leerzeichen: „Vorlese-Tempo“ → „vorlese tempo“. */
export function norm(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const worte = (s: string): string[] => norm(s).split(" ").filter((w) => w.length > 0);

/** Steht `kette` (in Worten) als zusammenhängende Folge in `text`? */
function enthaelt(text: readonly string[], kette: readonly string[]): boolean {
  if (kette.length === 0) return false;
  for (let i = 0; i + kette.length <= text.length; i++) {
    if (kette.every((w, j) => text[i + j] === w)) return true;
  }
  return false;
}

/* ── Der Wert und seine Wahl ────────────────────────────────────────── */

export type Werte = Record<string, unknown>;

export type Wahl = { ok: true; wert: unknown } | { ok: false; grund: string };

export interface Eintrag {
  /** Das Hauptfeld in `users.settings` — der Schlüsselname im Protokoll. */
  kennung: string;
  /** So heißt die Einstellung in der Oberfläche (und in jedem Satz). */
  titel: string;
  /** Unter diesen Namen wird sie gefunden. Einzelne Wörter zählen nur als GANZER Name. */
  woerter: readonly string[];
  /** Wo man sie selbst umstellt: „Einstellungen → Mitteilungen“. */
  ort: string;
  /** Alle Felder von `users.settings`, die ein Umstellen schreibt. */
  felder: readonly string[];
  /** Nur für das Hauptkonto (Konto 1). */
  nurKonto1?: boolean;
  /** Der Wert, wie er im Speicher steht — mit der Vorgabe, wenn nichts steht. */
  lese(s: Werte): unknown;
  /** Der Wert in Worten: „an“, „1,4×“, „Dunkel“. */
  zeige(wert: unknown): string;
  /** Aus dem, was gesagt wurde, den Wert wählen. */
  waehle(roh: string, konto1: boolean): Wahl;
  /** Die Felder, die für diesen Wert geschrieben werden. */
  schreibe(wert: unknown): Werte;
  /** Was die Wahl dem Nutzer sagt, wenn kein Wert passt: die erlaubten Werte. */
  erlaubt(konto1: boolean): string;
  /**
   * Hat diese Richtung Außenwirkung? Dann ändert das Werkzeug erst nach einem Ja in einem SPÄTEREN Zug
   * (zweistufig, tools/versand-bestaetigung.ts). Ausschalten bleibt sofort möglich.
   */
  heikel?(vorher: unknown, nachher: unknown): boolean;
}

/** Der Weg zu einer Seite in Worten. */
export function seitenOrt(id: SeitenId): string {
  return `Einstellungen → ${EINST_SEITEN.find((s) => s.id === id)?.titel ?? "Einstellungen"}`;
}

/** Füllwörter, die an einem Wert hängen dürfen: „wieder aus“, „bitte an“. */
const FUELLWOERTER = new Set(["wieder", "jetzt", "bitte", "mal", "doch", "auf", "dann"]);

/**
 * Das, was als Wert gesagt wurde, als Wortliste (klein, ohne Satzzeichen, ohne Füllwörter) — oder `null`, wenn nichts übrig bleibt.
 *
 * ⚠️ Danach nehmen die Wahlen NUR GESCHLOSSENE Wertmengen an: die ganze Liste muss genau EIN Wert sein (ein Schaltwort, eine
 *    Zahl, ein Name aus der Liste). Eine Verneinung („aus, keine Anrufe mehr“, „kein Auto“, „aus statt Auto“, „nicht so
 *    schnell“) oder ein zweiter Wert („von 1,4 auf 1,0“, „3 Sekunden, nicht auto“) passt in keine Liste und heißt „kein-wert“ —
 *    Vespera fragt nach, statt zu raten. Vorher suchte der Leser Schaltwörter irgendwo im Satz und überlas die Verneinung.
 */
export function lesWert(roh: unknown): string[] | null {
  const text = typeof roh === "string" ? roh : typeof roh === "number" || typeof roh === "boolean" ? String(roh) : null;
  if (text === null) return null;
  const ohne = worte(text).filter((x) => !FUELLWOERTER.has(x));
  return ohne.length > 0 ? ohne : null;
}

const JA = ["ein", "an", "anschalten", "einschalten", "eingeschaltet", "angeschaltet", "aktiv", "aktiviert", "aktivieren", "ja", "true", "on", "erlauben", "erlaubt", "1"];
const NEIN = ["aus", "ausschalten", "ausgeschaltet", "abschalten", "abgeschaltet", "deaktivieren", "deaktiviert", "nein", "false", "off", "verbieten", "verboten", "inaktiv", "0"];
/** „aus“ in allen Schreibweisen — Schalter und Stille meinen dasselbe (ohne „nein“, das bei der Stille nichts sagt). */
const AUS_WOERTER = NEIN.filter((x) => x !== "nein" && x !== "verbieten" && x !== "verboten");

/** „ein“/„aus“ — nur, wenn der GANZE Wert genau ein Schaltwort ist (siehe `lesWert`). */
export function leseSchalter(roh: unknown): boolean | null {
  const w = lesWert(roh);
  if (w?.length !== 1) return null;
  const x = w[0] as string;
  return JA.includes(x) ? true : NEIN.includes(x) ? false : null;
}

function schalter(o: {
  kennung: string;
  titel: string;
  woerter: readonly string[];
  ort: string;
  vorgabe: boolean;
  nurKonto1?: boolean;
  /** Einschalten hat Außenwirkung (erst nach einem Ja im nächsten Zug). */
  heikelEin?: boolean;
}): Eintrag {
  return {
    kennung: o.kennung,
    titel: o.titel,
    woerter: o.woerter,
    ort: o.ort,
    felder: [o.kennung],
    ...(o.nurKonto1 ? { nurKonto1: true } : {}),
    lese: (s) => (typeof s[o.kennung] === "boolean" ? s[o.kennung] : o.vorgabe),
    zeige: (w) => (w === true ? "an" : "aus"),
    waehle: (roh) => {
      const b = leseSchalter(roh);
      return b === null ? { ok: false, grund: "kein-wert" } : { ok: true, wert: b };
    },
    schreibe: (w) => ({ [o.kennung]: w }),
    erlaubt: () => "an oder aus",
    ...(o.heikelEin ? { heikel: (_v: unknown, n: unknown) => n === true } : {}),
  };
}

interface Option {
  wert: string;
  /** Wie die Oberfläche es nennt. */
  label: string;
  /** Weitere Namen dafür (Wörter oder Wortfolgen). */
  woerter?: readonly string[];
}

function auswahl(o: {
  kennung: string;
  titel: string;
  woerter: readonly string[];
  ort: string;
  vorgabe: string;
  optionen: readonly Option[];
  /** Zusätzlich zu `kennung` geschrieben. */
  zusatz?: Werte;
}): Eintrag {
  const finde = (w: unknown): Option | undefined => o.optionen.find((x) => x.wert === w);
  return {
    kennung: o.kennung,
    titel: o.titel,
    woerter: o.woerter,
    ort: o.ort,
    felder: [o.kennung, ...Object.keys(o.zusatz ?? {})],
    lese: (s) => (finde(s[o.kennung]) ? s[o.kennung] : o.vorgabe),
    zeige: (w) => finde(w)?.label ?? String(w),
    waehle: (roh) => {
      const w = lesWert(roh)?.join(" ");
      if (!w) return { ok: false, grund: "kein-wert" };
      // Geschlossen: der GANZE Wert ist der Name einer Option (Titel, Kennung oder ein Name der Liste).
      const treffer = o.optionen.filter((x) => [x.label, x.wert, ...(x.woerter ?? [])].map(norm).includes(w));
      const einziger = treffer.length === 1 ? treffer[0] : undefined;
      return einziger ? { ok: true, wert: einziger.wert } : { ok: false, grund: "kein-wert" };
    },
    schreibe: (w) => ({ [o.kennung]: w, ...(o.zusatz ?? {}) }),
    erlaubt: () => o.optionen.map((x) => x.label).join(", "),
  };
}

/* ── Die Einstellungen ──────────────────────────────────────────────── */

/**
 * Die Tempo-Namen des Blatts der Antwortlänge (frontend/lib/schnell-einstellungen.ts,
 * TEMPO_STUFEN) — ein Test hält sie gleich.
 */
export const TEMPO_NAMEN: ReadonlyArray<{ wert: number; name: string }> = [
  { wert: 1, name: "Normal" },
  { wert: 1.2, name: "Zügig" },
  { wert: 1.4, name: "Schnell" },
  { wert: 1.6, name: "Sehr schnell" },
];

/** „1,4“ — wie man ein Tempo liest. */
const tempoZahl = (t: number): string => t.toLocaleString("de-AT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Der Wert als Text für eine Zahl: klein, getrimmt, ohne Füllwörter („wieder 1,4“ → „1,4“). */
const ZAHLWOERTER: Readonly<Record<string, string>> = {
  null: "0", eins: "1", zwei: "2", drei: "3", vier: "4", fuenf: "5", fünf: "5", sechs: "6", sieben: "7", acht: "8", neun: "9",
};

const zahlText = (roh: unknown): string =>
  (typeof roh === "number" ? String(roh) : typeof roh === "string" ? roh : "")
    .toLowerCase()
    .replace(/\b(wieder|jetzt|bitte|mal|doch|auf|dann)\b/g, " ")
    // „eins komma vier“ → „1,4“, „zwei Sekunden“ → „2 sekunden“ (nur Wörter, die EINE Ziffer sind)
    .replace(/\b(null|eins|zwei|drei|vier|fuenf|fünf|sechs|sieben|acht|neun)\b/g, (z) => ZAHLWOERTER[z] ?? z)
    .replace(/\s*komma\s*/g, ",")
    .replace(/\s+/g, " ")
    .trim();

const TEMPO: Eintrag = {
  kennung: "speed",
  titel: "Vorlese-Tempo",
  woerter: ["vorlese tempo", "tempo", "vorlesetempo", "sprechtempo", "sprechgeschwindigkeit", "geschwindigkeit", "tempo beim vorlesen", "speed"],
  ort: seitenOrt("stimme"),
  felder: ["speed"],
  lese: (s) => vorleseTempo(s.speed) ?? 1,
  zeige: (w) => {
    const t = typeof w === "number" ? w : 1;
    const name = TEMPO_NAMEN.find((n) => Math.abs(n.wert - t) < 0.001)?.name;
    return `${tempoZahl(t)}×${name ? ` (${name})` : ""}`;
  },
  waehle: (roh) => {
    // Geschlossen (siehe `lesWert`): ein Tempo-Name ODER genau eine Zahl — „von 1,4 auf 1,0“ und „nicht so schnell“ sind keine Wahl.
    const w = lesWert(roh);
    if (!w) return { ok: false, grund: "kein-wert" };
    const name = TEMPO_NAMEN.find((n) => norm(n.name) === w.join(" "));
    if (name) return { ok: true, wert: name.wert };
    const zahl = /^(\d+(?:[.,]\d+)?)\s*(?:x|×|fach|-fach)?$/.exec(zahlText(roh));
    if (!zahl) return { ok: false, grund: "kein-wert" };
    const t = Number((zahl[1] as string).replace(",", "."));
    if (!Number.isFinite(t) || t < VORLESE_TEMPO.min - 1e-9 || t > VORLESE_TEMPO.max + 1e-9) {
      return { ok: false, grund: "ausserhalb" };
    }
    const geraster = vorleseTempo(t);
    return geraster === null ? { ok: false, grund: "ausserhalb" } : { ok: true, wert: geraster };
  },
  schreibe: (w) => ({ speed: w }),
  erlaubt: () => `${tempoZahl(VORLESE_TEMPO.min)} bis ${tempoZahl(VORLESE_TEMPO.max)} in Schritten von 0,1, oder Normal, Zügig, Schnell, Sehr schnell`,
};

const stilleText = (ms: number): string => (ms === STILLE_AUTO_MS ? "Auto" : ms > 0 ? `${Math.round(ms / 1000)} Sekunden` : "aus");

/** Zehnerzahlwörter, die für die langen Stufen gebraucht werden („dreißig Sekunden“ → „30 sekunden“). Ganzes Wort, sonst nichts. */
const STILLE_ZAHLWOERTER: Readonly<Record<string, string>> = { zehn: "10", zwanzig: "20", dreißig: "30", dreissig: "30", sechzig: "60" };
// ⚠️ VOR `zahlText`: dessen „drei“ → „3“ risse „dreißig“ auseinander (\b zählt ß nicht als Buchstabe).
const stilleZahlwoerter = <T>(t: T): T | string => (typeof t !== "string" ? t : t.replace(/\b(zehn|zwanzig|dreißig|dreissig|sechzig)\b/g, (z) => STILLE_ZAHLWOERTER[z] ?? z));

const STILLE: Eintrag = {
  kennung: "sendeStilleMs",
  titel: "Absenden nach Stille",
  woerter: ["absenden nach stille", "nach stille", "stille", "stille absenden", "sende stille", "automatisch absenden", "von selbst absenden", "stillezeit"],
  ort: seitenOrt("sprechen"),
  felder: ["sendeStilleMs"],
  lese: (s) => (typeof s.sendeStilleMs === "number" && Number.isFinite(s.sendeStilleMs) ? sendeStilleLesen(Math.round(s.sendeStilleMs)) : 0),
  zeige: (w) => stilleText(typeof w === "number" ? w : 0),
  waehle: (roh, konto1) => {
    // Geschlossen (siehe `lesWert`): GENAU „aus“, „auto“ oder eine Zahl (Sekunden oder ms). „kein Auto“, „aus statt Auto“,
    // „3 Sekunden, nicht auto“ sind keine Wahl — Vespera fragt nach, statt Auto (Mitschrift an Jev) zu raten.
    const lw = lesWert(roh);
    const w = lw?.join(" ");
    if (!lw || !w) return { ok: false, grund: "kein-wert" };
    if (w === "auto" || w === "automatisch") {
      // „Auto“ schickt die Mitschrift an einen fremden Dienst (Jev) — nur Konto 1, wie in der Oberfläche.
      return konto1 ? { ok: true, wert: STILLE_AUTO_MS } : { ok: false, grund: "auto-nur-konto-1" };
    }
    if (lw.length === 1 && AUS_WOERTER.includes(w)) return { ok: true, wert: 0 };
    // „2“, „2 Sekunden“, „zwei“, „2,0“ / „2.0“ (nur wenn es genau die ganze Zahl ist: „2,5“ bleibt keine Wahl)
    // Ticket 3176: „eine Minute“, „halbe Minute“, „zehn“, „dreißig Sekunden“, „sechzig“ — nur GANZE Angaben, sonst weiter keine Wahl.
    const text = zahlText(stilleZahlwoerter(typeof roh === "string" ? roh.toLowerCase() : roh));
    const minute = /^(?:(?:1|eine?) )?(halbe )?(?:minute|min)$/.exec(text);
    const zahl = /^(\d+)(?:[.,]0+)?\s*(?:sekunden|sekunde|sek|s)?$/.exec(text);
    if (!zahl && !minute) return { ok: false, grund: "kein-wert" };
    const n = zahl ? Number(zahl[1]) : 0;
    const ms = minute ? (minute[1] ? 30_000 : 60_000) : n >= 100 ? n : n * 1000;
    return SENDE_STILLE_STUFEN.includes(ms) && ms > 0 ? { ok: true, wert: ms } : { ok: false, grund: "ausserhalb" };
  },
  schreibe: (w) => ({ sendeStilleMs: w }),
  erlaubt: (konto1) => `aus, 2, 3, 5, 10, 30 oder 60 Sekunden${konto1 ? " oder Auto" : ""}`,
  // Auto schickt die Mitschrift an einen fremden Dienst (Jev): erst nach einem Ja im nächsten Zug.
  heikel: (_v, n) => n === STILLE_AUTO_MS,
};

/** Die acht Stimmen, die die Oberfläche zur Wahl zeigt (frontend/lib/settings.ts: Gemini zwei, Soniox sechs) — ein Test hält sie gleich. */
export const STIMMEN: ReadonlyArray<{ id: string; label: string }> = [
  { id: "de-DE-Chirp3-HD-Aoede", label: "Aoede" },
  { id: "de-DE-Chirp3-HD-Charon", label: "Charon" },
  { id: "soniox:Grace", label: "Grace" },
  { id: "soniox:Iris", label: "Iris" },
  { id: "soniox:Daniel", label: "Daniel" },
  { id: "soniox:Nathan", label: "Nathan" },
  { id: "soniox:Marlene", label: "Marlene" },
  { id: "soniox:Konrad", label: "Konrad" },
];

const STIMME: Eintrag = {
  kennung: "voiceId",
  titel: "Vorlese-Stimme",
  woerter: ["vorlese stimme", "stimme", "bot stimme", "vorlesestimme", "stimme beim vorlesen", "stimme zum vorlesen"],
  ort: seitenOrt("stimme"),
  felder: ["voiceId", "readVoiceChosen"],
  // Eine Stimme steht nur dann „gewählt“, wenn die Wahl bewusst getroffen wurde (readVoiceChosen), wie die Oberfläche es liest.
  lese: (s) => (s.readVoiceChosen === true && typeof s.voiceId === "string" ? s.voiceId : null),
  zeige: (w) => (w === null ? "keine gewählt (Standard)" : (STIMMEN.find((x) => x.id === w)?.label ?? "eine andere Stimme")),
  waehle: (roh) => {
    // Geschlossen: der GANZE Wert ist der Name einer Stimme.
    const w = lesWert(roh)?.join(" ");
    const treffer = w ? STIMMEN.filter((x) => norm(x.label) === w) : [];
    const einzige = treffer.length === 1 ? treffer[0] : undefined;
    return einzige ? { ok: true, wert: einzige.id } : { ok: false, grund: "kein-wert" };
  },
  schreibe: (w) => ({ voiceId: w, readVoiceChosen: true }),
  erlaubt: () => STIMMEN.map((x) => x.label).join(", "),
};

/** Emoji-Stufe (Einstellungen → Sprechen / Blatt der Antwortlänge): aus, dezent, reichlich — ein Feld, drei Werte (shared/emoji-stufe.ts). */
const EMOJI_NAMEN: Readonly<Record<EmojiStufenName, string>> = { aus: "aus", dezent: "dezent", reichlich: "reichlich" };
const EMOJI: Eintrag = {
  kennung: "emojisInReplies",
  titel: "Emojis in Antworten",
  woerter: ["emojis in antworten", "emojis", "emoji", "emoji stufe", "emojis in vesperas antworten", "smileys"],
  ort: seitenOrt("sprechen"),
  felder: ["emojisInReplies"],
  lese: (s) => emojiStufe(s.emojisInReplies),
  zeige: (w) => EMOJI_NAMEN[emojiStufenName(w)],
  waehle: (roh) => {
    // Geschlossen: der GANZE Wert ist „aus“, „keine“, „dezent“ oder „reichlich“ — nie ein bloßes „ein“ (welche Stufe?).
    const lw = lesWert(roh);
    const w = lw?.join(" ");
    if (!lw || !w) return { ok: false, grund: "kein-wert" };
    if (w === "dezent") return { ok: true, wert: true };
    if (w === "reichlich") return { ok: true, wert: "reichlich" };
    if (w === "keine" || (lw.length === 1 && AUS_WOERTER.includes(w))) return { ok: true, wert: false };
    return { ok: false, grund: "kein-wert" };
  },
  schreibe: (w) => ({ emojisInReplies: istEmojiStufe(w) ? w : false }),
  erlaubt: () => EMOJI_STUFEN.map((s) => EMOJI_NAMEN[emojiStufenName(s)]).join(", "),
};

export const EINSTELLUNGEN: readonly Eintrag[] = [
  schalter({
    kennung: "anrufErlaubt",
    titel: "Vespera darf mich anrufen",
    // 05.10.2026 (Ticket 2994): dazu, wie man den Schalter sonst nennt — „Anruf-Schalter“, „Anrufe erlauben“, „dass Vespera mich anrufen darf“.
    woerter: [
      "vespera darf mich anrufen", "darf mich anrufen", "anrufe von vespera", "vespera ruft an", "anrufen", "anrufe", "anruf", "anruf erlaubt",
      "anruf schalter", "anrufschalter", "vespera darf anrufen", "anrufe erlauben", "anruferlaubnis", "mich anrufen darf", "dich anrufen darf",
    ],
    ort: seitenOrt("mitteilungen"),
    vorgabe: false,
    nurKonto1: true,
    // Anrufe erlauben hat Außenwirkung (Vespera ruft von selbst an): erst nach einem Ja im nächsten Zug.
    heikelEin: true,
  }),
  TEMPO,
  STILLE,
  schalter({
    kennung: "tippenVorlesen",
    titel: "Getipptes vorlesen",
    woerter: ["getipptes vorlesen", "getippte fragen vorlesen", "getippte antworten vorlesen", "getipptes laut beantworten", "tippen vorlesen"],
    // 15.10.2026: dort im Block „Schnell einstellen“ (unter den Aktionen); die Einstellungsseite hat den Schalter (noch) nicht.
    ort: "das Plus-Menü im Chat, unter „Schnell einstellen“",
    vorgabe: true,
  }),
  STIMME,
  auswahl({
    kennung: "themeMode",
    titel: "Helligkeit",
    woerter: ["helligkeit", "hell oder dunkel", "darstellung", "dunkelmodus", "dark mode", "hellmodus", "modus"],
    ort: seitenOrt("darstellung"),
    vorgabe: "auto",
    optionen: [
      { wert: "auto", label: "Automatisch", woerter: ["system", "auto"] },
      { wert: "light", label: "Hell", woerter: ["light", "light mode", "lightmode", "hellmodus", "tag"] },
      { wert: "dark", label: "Dunkel", woerter: ["dark", "dark mode", "darkmode", "dunkelmodus", "nacht"] },
    ],
  }),
  auswahl({
    kennung: "themePalette",
    titel: "Farbpalette",
    woerter: ["farbpalette", "palette", "farbe", "farbschema", "farben"],
    ort: seitenOrt("darstellung"),
    vorgabe: "salbei",
    optionen: [
      { wert: "salbei", label: "Salbei" },
      { wert: "wald", label: "Wald" },
      { wert: "ozean", label: "Ozean" },
      { wert: "sand", label: "Sand" },
    ],
  }),
  // 11.10.2026 (Ticket 3077): Martin sagte zweimal „keine Probestücke mehr“ — das hält nur ein Schalter fest. „an“ heißt: gleich das
  // ganze Lied, nie ein Probestück. Bewusst NUR Namen mit „ohne“/„keine“: ein bloßes „Probestück aus“ wäre die umgekehrte Richtung.
  // Ohne Rückfrage: das Ja zu jedem Lied bleibt (Knopf oder Wort), der Schalter spart nur den Umweg über die Probe.
  schalter({
    kennung: MUSIK_OHNE_PROBE_FELD,
    titel: "Musik ohne Probestück",
    woerter: [
      "musik ohne probestück", "musik ohne probe", "ohne probestück", "ohne probestücke", "ohne probe", "lieder ohne probestück",
      "lieder ohne probe", "keine probestücke", "keine probestücke mehr", "kein probestück", "kein probestück mehr", "keine probelieder",
      "keine vorschau", "keine vorschau mehr", "musik ohne vorschau",
    ],
    ort: seitenOrt("stimme"),
    vorgabe: false,
  }),
  // 13.10.2026 (Ticket 2914, Abgleich gegen die Einstellungsseite): reine Anzeige- und Komfortwerte — ein Irrtum ist harmlos
  // und geht mit einem Zuruf zurück. Der Server-Filter (settings-sanitize.ts) lässt genau diese Wertemengen durch.
  EMOJI,
  auswahl({
    kennung: "szeneJahreszeit",
    titel: "Jahreszeit der Szene",
    woerter: ["jahreszeit der szene", "jahreszeit", "szene jahreszeit", "szene auf zuhause", "jahreszeit zuhause"],
    ort: seitenOrt("darstellung"),
    vorgabe: JAHRESZEIT_VORGABE,
    optionen: [
      { wert: "auto", label: "Automatisch", woerter: ["kalender", "auto"] },
      ...JAHRESZEITEN.map((j) => ({ wert: j, label: JAHRESZEIT_NAME[j] })),
    ],
  }),
  auswahl({
    kennung: "zuhauseStil",
    titel: "Zuhause-Stil",
    woerter: ["zuhause stil", "stil von zuhause", "aussehen von zuhause", "zuhause aussehen", "zuhause"],
    ort: seitenOrt("darstellung"),
    vorgabe: "blatt",
    optionen: [
      { wert: "blatt", label: "Blatt" },
      { wert: "kacheln", label: "Kacheln" },
    ],
  }),
  // Blendet beim Sprechen nur die Anzeige des Textes aus; die Erkennung läuft weiter (settings-view.ts, „Mitschrift anzeigen“).
  schalter({
    kennung: "mitschriftZeigen",
    titel: "Mitschrift anzeigen",
    woerter: ["mitschrift anzeigen", "mitschrift zeigen", "mitschrift beim sprechen", "text beim sprechen", "text beim sprechen anzeigen"],
    ort: seitenOrt("sprechen"),
    vorgabe: true,
  }),
  // 13.10.2026: Testschalter für den nativen Vorleser der iPhone-App (Antworten im Hintergrund, z. B. unter der Navigation).
  // Harmlos und mit einem Wort zurück; Vorgabe aus, greift nur bei verborgener Seite. Ohne Rückfrage, Kanal nur „app“ wie alle.
  schalter({
    kennung: VORLESER_NATIV_FELD,
    titel: "Nativer Vorleser (Test)",
    woerter: ["nativer vorleser", "nativen vorleser", "antworten im hintergrund vorlesen", "im hintergrund vorlesen"],
    ort: seitenOrt("versuche"),
    vorgabe: false,
  }),
];

/* ── Die harten Sperren ─────────────────────────────────────────────── */

export interface Sperre {
  id: string;
  /** Wer eines dieser Wörter nennt, bekommt die Absage — auch mit genauem Namen eines gesperrten Felds. */
  woerter: readonly string[];
  ort: string;
  /** Warum — für den Satz und für das Protokoll (Wort, nie ein Inhalt). */
  grund: string;
}

/**
 * Was Vespera nie per Zuruf umstellt. Zugangsdaten, Konto, Löschen, Kopplungen,
 * Modell und Kosten sind hier nicht „noch nicht dran“, sondern bewusst draußen:
 * eine falsche Stellung dort ist nicht mit einem Zuruf zurück (Schlüssel weg,
 * Daten weg, Geld ausgegeben, Kopplung fremd).
 *
 * ⚠️ Die Wörter prüft `loeseAuf` VOR jedem Treffer in der weißen Liste: „Telegram-Anrufe“
 *    trifft „Telegram“, nicht „Anrufe“.
 */
export const HART_GESPERRT: readonly Sperre[] = [
  {
    id: "zugang",
    woerter: ["passwort", "kennwort", "token", "schluessel", "api key", "apikey", "zugangsdaten", "zugang", "login", "anmeldung", "secret", "geheim", "geheimnis", "zwei faktor", "2fa", "code"],
    ort: seitenOrt("modelle"),
    grund: "zugangsdaten",
  },
  {
    id: "konto",
    woerter: ["abmelden", "logout", "ausloggen", "konto", "account", "zuruecksetzen", "zuruecksetzung", "reset", "standard", "werkseinstellung", "version", "update", "email", "mail"],
    ort: seitenOrt("modelle"),
    grund: "konto",
  },
  {
    id: "loeschen",
    woerter: ["loeschen", "entfernen", "vergessen", "verlauf", "archiv", "mistkuebel", "papierkorb", "daten", "gedaechtnis", "erinnerungen", "erinnerung"],
    ort: seitenOrt("verlauf"),
    grund: "loeschen",
  },
  {
    id: "kopplung",
    woerter: ["telegram", "desktop", "mac", "kalender", "ical", "feed", "feeds", "verbinden", "verbindung", "verbindungen", "koppeln", "kopplung", "google", "gmail", "erweiterung", "browser", "standort", "whatsapp", "panel", "trello"],
    ort: seitenOrt("verbindungen"),
    grund: "kopplung",
  },
  {
    id: "modell",
    woerter: ["modell", "modelle", "claude", "opus", "sonnet", "haiku", "gemini", "grok", "openai", "kosten", "guthaben", "abo", "denktiefe", "heavy", "qualitaet", "eskalation", "fallback", "tarif", "preis", "bezahlen"],
    ort: seitenOrt("modelle"),
    grund: "modell-kosten",
  },
  {
    // Gerätewerte: Mikro und Aufnahme gehören dem Gerät, die Entscheidung dem Menschen davor.
    // 05.10.2026: dazu „Hey Vespera nur mit meiner Stimme“ — ein Schutz (wer Vespera wecken darf), den niemand per Zuruf abschaltet.
    // 07.10.2026 (Ticket 3048): dazu „Jev entscheidet“ — die Stufe von „Mikro von selbst öffnen“; ohne das Wort lief der
    // Name ins Leere („unbekannt“), jetzt nennt die Absage den Weg dorthin.
    id: "geraet",
    woerter: ["wach wort", "wachwort", "hey vespera", "mikro", "mikrofon", "aufnahme", "aufzeichnen", "immer aufzeichnen", "durchgehend", "tagesaufnahme", "alltag", "lautsprecher", "empfindlichkeit", "nur meine stimme", "nur mit meiner stimme", "jev entscheidet"],
    ort: seitenOrt("sprechen"),
    grund: "geraet",
  },
  {
    // 05.10.2026: die Grenzen der Anrufe (Ruhezeit, Tageslimit) SCHÜTZEN — lockern gehört in die Hand des Menschen.
    // Der Schalter selbst („Vespera darf mich anrufen“) steht in der weißen Liste, mit Rückfrage beim Einschalten.
    id: "anrufgrenzen",
    woerter: ["ruhezeit", "nachtruhe", "tageslimit", "anruflimit", "anruf limit", "anrufe pro tag", "anrufe am tag"],
    ort: seitenOrt("mitteilungen"),
    grund: "schutz",
  },
  {
    // 05.10.2026: Versuchsschalter am Ton- und Antwortweg (Soniox-Strom, Tempo der Stimme, Pausen kürzen, Sofort-Antwort).
    // Jeder verändert, wie Vespera klingt oder antwortet, und gehört zu einer Hörprobe am Gerät — nie auf Zuruf.
    id: "technik",
    woerter: ["beta", "datenstrom", "pcm", "pausen kuerzen", "stille kuerzen", "tempo von der stimme", "tempo nativ", "sofort antwort", "vorantwort", "vormodell", "vor modell"],
    // 16.10.2026: die Versuchsschalter stehen seither auf der Seite „Erweitert“ (Seite `versuche`).
    ort: seitenOrt("versuche"),
    grund: "technik",
  },
  {
    // 06.10.2026 (Ticket 2738): „Kurz Bescheid geben, wenn die Antwort dauert“ (Warte-Brücke) — derselbe Grund wie oben: er
    // verändert, wie Vespera antwortet, und gehört zu einer Hörprobe. Der Schalter steht unter „Sprechen“.
    id: "warte-satz",
    woerter: ["kurz bescheid geben", "wenn die antwort dauert", "sofort satz", "sofortsatz", "warte satz", "wartesatz", "warte bruecke", "wartebruecke", "lange wartezeit"],
    ort: seitenOrt("sprechen"),
    grund: "technik",
  },
];

/**
 * Jedes andere Feld von `users.settings`: nicht freigegeben. Die Gruppe sagt,
 * warum — und der Test hält fest, dass jedes Feld der Oberfläche (und des
 * Servers) hier ODER in einem Eintrag steht. Ein neues Feld bricht ihn, bis
 * jemand entschieden hat.
 */
export const GESPERRTE_FELDER: Readonly<Record<string, "modell" | "geraet" | "datenschutz" | "schutz" | "nicht-freigegeben">> = {
  // Modelle und Geld
  model: "modell", fallbackModel: "modell", cliModel: "modell", cliExecutor: "modell", autoEscalate: "modell",
  qualityMode: "modell", heavyModel: "modell", starkesModell: "modell", tiefeDenkstufe: "modell", cliChat: "modell",
  aboModell: "modell", modellBevorzugt: "modell", voiceMode: "modell", realtimeVoice: "modell", realtimeModel: "modell",
  ttsProvider: "modell", googleTtsVoice: "modell", ttsDirektBeta: "modell", passiveKnowledge: "modell",
  // 05.10.2026: Technik-Schalter am Ton- und Antwortweg (Tickets 2919, 2738) — Versuche mit Hörprobe, nie auf Zuruf
  ttsPcmBeta: "modell", ttsTempoNativ: "modell", ttsStilleKuerzen: "modell", vorantwortBeta: "modell",
  // 06.10.2026: der Warte-Satz der Warte-Brücke (Ticket 2738) — eigener Schalter, dieselbe Einordnung wie die Vorantwort
  warteSatz: "modell",
  // 07.10.2026: ElevenLabs v4 für die Meditation (nur Konto 1) — kostet Guthaben, nie auf Zuruf
  meditationElevenStimme: "modell",
  // Mikro und Aufnahme am Gerät
  alwaysRecord: "geraet", wachwort: "geraet", sttSensitivity: "geraet", autoMikroNachFrage: "geraet", nachlauschMs: "geraet",
  // 14.10.2026: „Mikro beim Vorlesen offen halten (Test)“ — Gerätekippe der iPhone-App, nur in den Einstellungen wählbar
  mikroOffenBeimVorlesen: "geraet",
  // 19.10.2026: Mikro-Vorstart und Bereit-Ton — Gerätekippen des Auto-Mikros, in den Einstellungen wählbar
  mikroVorstart: "geraet", bereitTon: "geraet",
  // 14.10.2026: Antwortvorschau auf dem Sperrbildschirm (Build 110) — zeigt Text Fremden, nur in den Einstellungen wählbar
  antwortVorschauSperrbildschirm: "datenschutz",
  // Zustimmungen und Datenschutz — ein Widerruf gehört in die Hand des Menschen
  stimmungAusStimme: "datenschutz", wachwortNurMeineStimme: "datenschutz", useHdInPersona: "datenschutz",
  selbstermaechtigung: "datenschutz", achtsamkeit: "datenschutz", archivTage: "datenschutz",
  // 05.10.2026: Grenzen, die schützen (Ticket 2728: Ruhezeit und Tageslimit der Anrufe) — ein Zuruf darf sie nie lockern
  anrufRuheAb: "schutz", anrufRuheBis: "schutz", anrufTageslimit: "schutz",
  // Übrige: noch nicht geprüft
  narratorVoiceId: "nicht-freigegeben", hdChartView: "nicht-freigegeben", language: "nicht-freigegeben",
  autoConnect: "nicht-freigegeben", responseStyle: "nicht-freigegeben", pttMode: "nicht-freigegeben",
  antwortTiefe: "nicht-freigegeben", sendeWort: "nicht-freigegeben",
  abschiedErkennen: "nicht-freigegeben", rueckfallVoiceId: "nicht-freigegeben",
  meditationVoiceId: "nicht-freigegeben", meditationStil: "nicht-freigegeben", customVoices: "nicht-freigegeben",
  chatStyle: "nicht-freigegeben",
  // 06.10.2026: „Morse im Menü“ (Morse-Modul, nur Konto 1) — blendet nur den Menüpunkt aus; noch nicht geprüft
  morseLernen: "nicht-freigegeben",
  // 30.10.2026: Einführung und Stimme von Vespera 9.0 (nur Konto 1) — die Seite /neun schreibt sie, nie auf Zuruf
  neunStimme: "nicht-freigegeben", neunEinfuehrung: "nicht-freigegeben",
  // 15.10.2026: „Entwickler im Chat“ (Dreier-Modus) — lässt eine dritte Stimme in Martins Chat; nur Martin selbst schaltet, nie auf Zuruf
  entwicklerImChat: "schutz",
  // 18.10.2026: Hauptschalter „Entwickler“ — schaltet alle drei zugleich (shared/entwickler-im-chat.ts `entwicklerHauptAn`); nur Martin selbst
  entwicklerGesamt: "schutz",
  // 16.10.2026: „Zimmermann im Chat“ (shared/entwickler.ts) — eine weitere Stimme in Martins Chat; nur Martin selbst, nie auf Zuruf
  zimmermannImChat: "schutz",
  // 17.10.2026: „Hüter im Chat“ (Server 7) — wie der Zimmermann; nur Martin selbst, nie auf Zuruf
  hueterImChat: "schutz",
  // 17.10.2026: „Server-Netz“ (shared/server-netz.ts) — liest den Zustand der Server; nur Martin selbst, nie auf Zuruf
  serverNetz: "schutz",
  // 15.10.2026: „Entwickler vorlesen“ / „Stimme des Entwicklers“ (shared/schmied.ts) — gehört zum Dreier-Modus; nur Martin selbst
  entwicklerVorlesen: "schutz",
  // 19.10.2026: „Zimmermann und Hüter im Gespräch vorlesen“ — gehört zum Dreier-Modus; nur Martin selbst
  zusatzVorlesen: "schutz",
  // 17.10.2026: „Schmied-Stimme im Ganz-Modus“ ersetzt den Notschalter SCHMIED_VORLESEN_GANZ — nur Martin selbst
  schmiedStimmeGanz: "schutz",
  entwicklerStimme: "schutz",
  // 16.10.2026: „Stimme des Zimmermanns“ (Seite Entwickler-Team) — gehört zum Dreier-Modus wie die Stimme des Schmieds; nur Martin selbst
  zimmermannStimme: "schutz",
  // 30.10.2026: die V9-Wahlen aus dem Kennenlernen — nur über die Bestätigungskarte in /neun (server/neun/profil.ts), nie auf Zuruf
  neunAnredeForm: "nicht-freigegeben", neunAntwortlaenge: "nicht-freigegeben",
  hueterStimme: "schutz",
  // 17.10.2026: Sprechtempo und Ausführlichkeit je Entwickler (Seite Entwickler-Team) — gehören zum Dreier-Modus; nur Martin selbst
  entwicklerTempo: "schutz",
  zimmermannTempo: "schutz",
  hueterTempo: "schutz",
  entwicklerAusfuehrlichkeit: "schutz",
  zimmermannAusfuehrlichkeit: "schutz",
  hueterAusfuehrlichkeit: "schutz",
  // 17.10.2026: „Selbstverbesserung“ (Phase 1, Vorschläge) — wer Vorschläge an Martin stellt, schaltet sie nicht selbst ein
  selbstverbesserung: "schutz",
};

/* ── Namen auflösen ─────────────────────────────────────────────────── */

export type Aufloesung =
  | { art: "treffer"; eintrag: Eintrag }
  | { art: "gesperrt"; sperre: Sperre }
  | { art: "mehrdeutig"; kandidaten: readonly Eintrag[] }
  | { art: "unbekannt"; ort: string };

/** Die Seite, die am besten zu den Wörtern passt — für „du findest es unter …“. */
function ortFuer(name: string): string {
  const w = worte(name).filter((x) => x.length >= 4);
  let beste: { id: SeitenId; punkte: number } | null = null;
  for (const s of EINST_SEITEN) {
    const heu = worte(`${s.titel} ${s.stichworte}`);
    const punkte = w.filter((x) => heu.includes(x)).length;
    if (punkte > 0 && (!beste || punkte > beste.punkte)) beste = { id: s.id, punkte };
  }
  return beste ? seitenOrt(beste.id) : "Einstellungen";
}

/**
 * Welche Einstellung ist gemeint?
 *
 *   1. der GENAUE Name (Titel, Schlüsselname oder ein Name der Liste) → Treffer
 *   2. ein Wort der harten Sperren → gesperrt (vor jedem Teiltreffer)
 *   3. eine MEHRWORT-Bezeichnung der Liste im Satz („Tempo beim Vorlesen“) → Treffer
 *      (einzelne Wörter reichen hier nie: „Stimmung aus der Stimme“ ist nicht „Stimme“)
 *   4. sonst: unbekannt, mit dem Weg zur passenden Seite
 */
export function loeseAuf(name: unknown): Aufloesung {
  const roh = typeof name === "string" ? name : "";
  const n = norm(roh);
  const w = n.length > 0 ? n.split(" ") : [];
  if (w.length === 0) return { art: "unbekannt", ort: "Einstellungen" };
  const namen = (e: Eintrag): string[] => [e.titel, e.kennung, ...e.woerter].map(norm);
  const genau = EINSTELLUNGEN.filter((e) => namen(e).includes(n));
  const g = genau[0];
  if (genau.length === 1 && g) return { art: "treffer", eintrag: g };
  if (genau.length > 1) return { art: "mehrdeutig", kandidaten: genau };
  const sperre = HART_GESPERRT.find((s) => s.woerter.some((x) => enthaelt(w, worte(x))));
  if (sperre) return { art: "gesperrt", sperre };
  const teil = EINSTELLUNGEN.filter((e) => namen(e).some((a) => a.includes(" ") && enthaelt(w, a.split(" "))));
  const t = teil[0];
  if (teil.length === 1 && t) return { art: "treffer", eintrag: t };
  if (teil.length > 1) return { art: "mehrdeutig", kandidaten: teil };
  return { art: "unbekannt", ort: ortFuer(roh) };
}

/** Ohne Wert (oder leer) wird nur gelesen — dieselbe Regel im Werkzeug und im Schutz vor Fremdtext. */
export function istLesen(wert: unknown): boolean {
  return wert === undefined || wert === null || (typeof wert === "string" && wert.trim() === "");
}

/**
 * Ein „Nein“ auf die Rückfrage: `bestaetigt: false` MIT der Kennung der Rückfrage. (Ticket 2875)
 *
 * Dieselbe Regel im Werkzeug und im Schutz vor Fremdtext (server/werkzeug-einordnung.ts, `harmlos`) — ein Besitzer.
 * Das Werkzeug tut bei einem solchen Aufruf NIE etwas anderes, als die Rückfrage zu verwerfen (sie ist danach nicht
 * mehr einlösbar): es liest keinen Wert, wählt keinen und schreibt nichts. Nur darum darf der Schutz sie auch nach
 * gelesenem Fremdtext durchlassen — ein Nein ist die sichere Richtung. Ohne Kennung ist es ein erster Aufruf wie jeder
 * andere: `bestaetigt: false` allein öffnet nichts.
 */
export function istAblehnung(bestaetigt: unknown, vorschlagId: unknown): boolean {
  return bestaetigt === false && typeof vorschlagId === "string";
}

/* ── Die Sätze ──────────────────────────────────────────────────────── */

/** „Zurück: sag „Vespera darf mich anrufen wieder aus“.“ — oder der Weg in die Einstellungen, wenn es keinen alten Wert gibt. */
function rueckwegSatz(e: Eintrag, vorher: unknown): string {
  if (vorher === null || vorher === undefined) return `Zurückstellen geht in ${e.ort}.`;
  return `Zurück: sag „${e.titel} wieder ${e.zeige(vorher)}“.`;
}

export function satzGeaendert(e: Eintrag, vorher: unknown, nachher: unknown): string {
  return `„${e.titel}“ steht jetzt auf ${e.zeige(nachher)} (vorher ${e.zeige(vorher)}). ${rueckwegSatz(e, vorher)}`;
}

/** Die Rückfrage vor einer Änderung mit Außenwirkung. */
export function satzFrage(e: Eintrag, nachher: unknown): string {
  return `Soll ich „${e.titel}“ auf ${e.zeige(nachher)} stellen? Sag Ja.`;
}

/** Was eine Einstellung ändern würde, für die Kontrollzeile eines offenen Vorschlags (null: unbekannt). */
export function beschreibeAenderung(kennung: unknown, wert: unknown): string | null {
  const e = EINSTELLUNGEN.find((x) => x.kennung === kennung);
  return e ? `Einstellung umstellen: „${e.titel}“ auf ${e.zeige(wert)}` : null;
}

export function satzUnveraendert(e: Eintrag, wert: unknown): string {
  return `„${e.titel}“ steht schon auf ${e.zeige(wert)} — nichts geändert.`;
}

/** Er hat die Rückfrage abgelehnt: es bleibt, wie es ist. */
export function satzBleibt(e: Eintrag, wert: unknown): string {
  return `„${e.titel}“ bleibt auf ${e.zeige(wert)} — nichts geändert.`;
}

export function satzGelesen(e: Eintrag, wert: unknown): string {
  return `„${e.titel}“ steht auf ${e.zeige(wert)}.`;
}

export function satzAbsage(name: string, a: Extract<Aufloesung, { art: "gesperrt" | "unbekannt" }>): string {
  const ort = a.art === "gesperrt" ? a.sperre.ort : a.ort;
  const was = name.trim().slice(0, 60) || "Das";
  return a.art === "gesperrt"
    ? `„${was}“ ändere ich nicht per Zuruf — du findest es unter ${ort}.`
    : `„${was}“ kann ich nicht ändern — du findest es unter ${ort}.`;
}

/**
 * Alle Einstellungen der weißen Liste mit ihren erlaubten Werten, für die Werkzeugbeschreibung (Ticket 3199:
 * „Wert“ war ein Fehlschlag, weil die Werte geraten werden mussten). Aus der Liste erzeugt, nie von Hand kopiert.
 * Ohne Hauptkonto-Werte (Auto): die Beschreibung gilt für alle Konten, die Antwort nennt den Rest.
 */
export function erlaubteWerteText(): string {
  return EINSTELLUNGEN.map((e) => `${e.titel}: ${e.erlaubt(false)}`).join("; ");
}
